import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import Nav from "@/components/Nav";
import { CheckCircle, XCircle, Storefront, PaintBrush } from "@phosphor-icons/react";

const STATUS_COLORS = {
  pending: "bg-amber-100 text-amber-800",
  approved: "bg-emerald-100 text-emerald-800",
  rejected: "bg-rose-100 text-rose-800",
};

export default function AdminApplications() {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [typeFilter, setTypeFilter] = useState("all");
  const [busy, setBusy] = useState(null); // id currently mutating

  const load = () => {
    setLoading(true);
    api.get("/admin/applications")
      .then(r => setApps(r.data))
      .catch(() => toast.error("Failed to load applications"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const approve = async (id) => {
    if (!confirm("Approve this application? An activation email will be sent to the applicant.")) return;
    setBusy(id);
    try {
      await api.post(`/admin/applications/${id}/approve`);
      toast.success("Approved. Invite email sent.");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Approve failed");
    } finally { setBusy(null); }
  };

  const reject = async (id) => {
    const reason = window.prompt("Optional rejection reason (shared with applicant):", "") || "";
    if (reason === null) return;
    setBusy(id);
    try {
      await api.post(`/admin/applications/${id}/reject`, { reason: reason || null });
      toast.success("Rejected. Applicant notified.");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reject failed");
    } finally { setBusy(null); }
  };

  const counts = useMemo(() => {
    const c = { total: apps.length, pending: 0, approved: 0, rejected: 0, vendor: 0, designer: 0 };
    apps.forEach(a => { c[a.status]++; c[a.type]++; });
    return c;
  }, [apps]);

  const filtered = useMemo(() => {
    return apps.filter(a =>
      (statusFilter === "all" || a.status === statusFilter) &&
      (typeFilter === "all" || a.type === typeFilter)
    );
  }, [apps, statusFilter, typeFilter]);

  return (
    <div className="min-h-screen bg-[var(--pf-bg)]">
      <Nav />
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-10">
        <div className="pf-label mb-3">Admin · Applications</div>
        <h1 className="text-4xl sm:text-5xl font-black tracking-tighter mb-8">Vendor & designer inbox.</h1>

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          {[
            ["Pending", counts.pending],
            ["Approved", counts.approved],
            ["Rejected", counts.rejected],
            ["Total", counts.total],
          ].map(([label, n]) => (
            <div key={label} className="border border-[var(--pf-border)] bg-white p-4">
              <div className="pf-label">{label}</div>
              <div className="text-3xl font-black tracking-tighter mt-1">{n}</div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-6">
          {["pending", "approved", "rejected", "all"].map(s => (
            <button key={s} onClick={() => setStatusFilter(s)} data-testid={`filter-status-${s}`}
              className={`px-3 py-1.5 text-xs uppercase tracking-widest font-bold border-2 ${
                statusFilter === s ? "bg-[var(--pf-text)] text-white border-[var(--pf-text)]" : "bg-white border-[var(--pf-border)] hover:border-[var(--pf-text)]"
              }`}>{s}</button>
          ))}
          <span className="mx-2 text-[var(--pf-text-3)]">·</span>
          {["all", "vendor", "designer"].map(t => (
            <button key={t} onClick={() => setTypeFilter(t)} data-testid={`filter-type-${t}`}
              className={`px-3 py-1.5 text-xs uppercase tracking-widest font-bold border-2 ${
                typeFilter === t ? "bg-[var(--pf-brand)] text-white border-[var(--pf-brand)]" : "bg-white border-[var(--pf-border)] hover:border-[var(--pf-brand)]"
              }`}>{t}</button>
          ))}
        </div>

        {loading ? (
          <div className="border border-dashed border-[var(--pf-border)] p-16 text-center text-[var(--pf-text-2)]">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="border border-dashed border-[var(--pf-border)] p-16 text-center text-[var(--pf-text-2)]" data-testid="no-applications">
            No {statusFilter === "all" ? "" : statusFilter} applications yet.
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map(a => {
              const isVendor = a.type === "vendor";
              const Icon = isVendor ? Storefront : PaintBrush;
              return (
                <div key={a.id} className="border border-[var(--pf-border)] bg-white p-5" data-testid={`application-${a.id}`}>
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex items-start gap-3">
                      <div className={`w-10 h-10 rounded flex items-center justify-center shrink-0 ${isVendor ? "bg-blue-100 text-blue-700" : "bg-purple-100 text-purple-700"}`}>
                        <Icon size={20} weight="fill" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="font-black text-lg tracking-tight">{a.name}</div>
                          <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded ${STATUS_COLORS[a.status]}`}>{a.status}</span>
                          <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded bg-gray-100 text-gray-800">{a.type}</span>
                        </div>
                        <div className="text-sm text-[var(--pf-text-2)] mt-1">
                          {a.email} {a.phone && <>· {a.phone}</>}
                        </div>
                        <div className="text-xs text-[var(--pf-text-3)]">
                          Applied {new Date(a.created_at).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                        </div>
                      </div>
                    </div>
                    {a.status === "pending" && (
                      <div className="flex gap-2 shrink-0">
                        <button onClick={() => approve(a.id)} disabled={busy === a.id} data-testid={`approve-${a.id}`}
                          className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs uppercase tracking-widest font-bold px-3 py-2 disabled:opacity-40">
                          <CheckCircle size={14} weight="fill" /> Approve
                        </button>
                        <button onClick={() => reject(a.id)} disabled={busy === a.id} data-testid={`reject-${a.id}`}
                          className="inline-flex items-center gap-1.5 bg-white hover:bg-rose-50 border-2 border-rose-500 text-rose-700 text-xs uppercase tracking-widest font-bold px-3 py-2 disabled:opacity-40">
                          <XCircle size={14} weight="fill" /> Reject
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Payload details */}
                  <div className="mt-4 grid sm:grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-2 text-sm">
                    {Object.entries(a.payload).map(([k, v]) => {
                      if (k === "name" || k === "email" || k === "phone" || !v || (Array.isArray(v) && v.length === 0)) return null;
                      const display = Array.isArray(v) ? v.join(", ") : String(v);
                      return (
                        <div key={k}>
                          <div className="pf-label">{k.replace(/_/g, " ")}</div>
                          <div className="text-sm break-words">{k.includes("url") || k === "website" ? (
                            <a href={display.startsWith("http") ? display : `https://${display}`} target="_blank" rel="noopener noreferrer" className="text-[var(--pf-brand)] underline break-all">{display}</a>
                          ) : display}</div>
                        </div>
                      );
                    })}
                  </div>

                  {a.reject_reason && (
                    <div className="mt-3 p-3 bg-rose-50 border-l-2 border-rose-500 text-sm text-rose-900">
                      <strong>Rejection reason:</strong> {a.reject_reason}
                    </div>
                  )}
                  {a.status === "approved" && a.invite_id && (
                    <div className="mt-3 text-xs text-[var(--pf-text-3)]">Invite ID: <code>{a.invite_id}</code> · Activation email sent</div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
